import { useEffect, useState } from "react";

import { useGulfCountryDetection } from "@/client/utils/gulf-countries-detector";

export type CountryData = {
  countryCode: string;
  countryName: string;
  flag: string;
  currency: string;
  timezone: string;
  city?: string;
  region?: string;
  isGulfCountry: boolean;
  loading: boolean;
  error?: string;
};

/**
 * Enhanced country detection hook that uses ipapi.co API
 * and provides comprehensive country data for all sections
 */
export function useDynamicCountryDetection(): CountryData {
  const [countryData, setCountryData] = useState<CountryData>({
    countryCode: "US",
    countryName: "United States",
    flag: "🇺🇸",
    currency: "USD",
    timezone: "America/New_York",
    isGulfCountry: false,
    loading: true,
  });

  // Use existing Gulf country detection for IP geolocation
  const { detection, loading: gulfLoading } = useGulfCountryDetection();

  useEffect(() => {
    if (!gulfLoading && detection) {
      // Convert country code to flag emoji
      const getCountryFlag = (countryCode: string): string => {
        const code = countryCode.toUpperCase();
        return [...code]
          .map((char) => String.fromCodePoint(0x1_f1_e6 + char.charCodeAt(0) - 65))
          .join("");
      };

      // Get country name from code - simplified list
      const getCountryName = (countryCode: string): string => {
        const countryNames: Record<string, string> = {
          US: "United States",
          GB: "United Kingdom",
          CA: "Canada",
          AU: "Australia",
          DE: "Germany",
          FR: "France",
          ES: "Spain",
          IT: "Italy",
          NL: "Netherlands",
          BE: "Belgium",
          CH: "Switzerland",
          AT: "Austria",
          SE: "Sweden",
          NO: "Norway",
          DK: "Denmark",
          FI: "Finland",
          PL: "Poland",
          CZ: "Czech Republic",
          HU: "Hungary",
          RO: "Romania",
          BG: "Bulgaria",
          HR: "Croatia",
          SI: "Slovenia",
          SK: "Slovakia",
          LT: "Lithuania",
          LV: "Latvia",
          EE: "Estonia",
          IE: "Ireland",
          PT: "Portugal",
          GR: "Greece",
          CY: "Cyprus",
          MT: "Malta",
          LU: "Luxembourg",
          IS: "Iceland",
          LI: "Liechtenstein",
          MC: "Monaco",
          SM: "San Marino",
          VA: "Vatican City",
          AD: "Andorra",
          JO: "Jordan",
          AE: "United Arab Emirates",
          SA: "Saudi Arabia",
          KW: "Kuwait",
          QA: "Qatar",
          BH: "Bahrain",
          OM: "Oman",
          IQ: "Iraq",
          SY: "Syria",
          LB: "Lebanon",
          PS: "Palestine",
          EG: "Egypt",
          LY: "Libya",
          TN: "Tunisia",
          DZ: "Algeria",
          MA: "Morocco",
          SD: "Sudan",
          SS: "South Sudan",
          ET: "Ethiopia",
          ER: "Eritrea",
          DJ: "Djibouti",
          SO: "Somalia",
          KE: "Kenya",
          UG: "Uganda",
          TZ: "Tanzania",
          RW: "Rwanda",
          BI: "Burundi",
          MW: "Malawi",
          ZM: "Zambia",
          ZW: "Zimbabwe",
          BW: "Botswana",
          NA: "Namibia",
          ZA: "South Africa",
          LS: "Lesotho",
          SZ: "Eswatini",
          MG: "Madagascar",
          MU: "Mauritius",
          SC: "Seychelles",
          KM: "Comoros",
          YT: "Mayotte",
          RE: "Réunion",
          IN: "India",
          PK: "Pakistan",
          BD: "Bangladesh",
          LK: "Sri Lanka",
          MV: "Maldives",
          NP: "Nepal",
          BT: "Bhutan",
          AF: "Afghanistan",
          IR: "Iran",
          TR: "Turkey",
          GE: "Georgia",
          AM: "Armenia",
          AZ: "Azerbaijan",
          KZ: "Kazakhstan",
          UZ: "Uzbekistan",
          TM: "Turkmenistan",
          TJ: "Tajikistan",
          KG: "Kyrgyzstan",
          MN: "Mongolia",
          CN: "China",
          TW: "Taiwan",
          HK: "Hong Kong",
          MO: "Macau",
          JP: "Japan",
          KR: "South Korea",
          KP: "North Korea",
          TH: "Thailand",
          VN: "Vietnam",
          LA: "Laos",
          KH: "Cambodia",
          MY: "Malaysia",
          SG: "Singapore",
          ID: "Indonesia",
          PH: "Philippines",
          BN: "Brunei",
          TL: "East Timor",
          MM: "Myanmar",
          NZ: "New Zealand",
          FJ: "Fiji",
          PG: "Papua New Guinea",
          SB: "Solomon Islands",
          VU: "Vanuatu",
          NC: "New Caledonia",
          PF: "French Polynesia",
          WS: "Samoa",
          TO: "Tonga",
          KI: "Kiribati",
          TV: "Tuvalu",
          NR: "Nauru",
          PW: "Palau",
          FM: "Micronesia",
          MH: "Marshall Islands",
          CK: "Cook Islands",
          NU: "Niue",
          TK: "Tokelau",
          AS: "American Samoa",
          GU: "Guam",
          MP: "Northern Mariana Islands",
          VI: "U.S. Virgin Islands",
          VG: "British Virgin Islands",
          AI: "Anguilla",
          AW: "Aruba",
          CW: "Curaçao",
          SX: "Sint Maarten",
          BQ: "Caribbean Netherlands",
          KY: "Cayman Islands",
          TC: "Turks and Caicos Islands",
          BM: "Bermuda",
          GL: "Greenland",
          PM: "Saint Pierre and Miquelon",
          BL: "Saint Barthélemy",
          MF: "Saint Martin",
          GP: "Guadeloupe",
          MQ: "Martinique",
          AN: "Netherlands Antilles",
          BR: "Brazil",
          AR: "Argentina",
          CL: "Chile",
          CO: "Colombia",
          PE: "Peru",
          VE: "Venezuela",
          UY: "Uruguay",
          PY: "Paraguay",
          BO: "Bolivia",
          EC: "Ecuador",
          GY: "Guyana",
          SR: "Suriname",
          GF: "French Guiana",
          FK: "Falkland Islands",
          GS: "South Georgia and the South Sandwich Islands",
          MX: "Mexico",
          GT: "Guatemala",
          BZ: "Belize",
          SV: "El Salvador",
          HN: "Honduras",
          NI: "Nicaragua",
          CR: "Costa Rica",
          PA: "Panama",
          CU: "Cuba",
          JM: "Jamaica",
          HT: "Haiti",
          DO: "Dominican Republic",
          PR: "Puerto Rico",
          TT: "Trinidad and Tobago",
          BB: "Barbados",
          AG: "Antigua and Barbuda",
          DM: "Dominica",
          GD: "Grenada",
          KN: "Saint Kitts and Nevis",
          LC: "Saint Lucia",
          VC: "Saint Vincent and the Grenadines",
          BS: "Bahamas",
          RU: "Russia",
          BY: "Belarus",
          UA: "Ukraine",
          MD: "Moldova",
          RS: "Serbia",
          ME: "Montenegro",
          BA: "Bosnia and Herzegovina",
          MK: "North Macedonia",
          AL: "Albania",
          XK: "Kosovo",
        };
        return countryNames[countryCode] || countryCode;
      };

      // Get currency from country code - simplified list
      const getCurrency = (countryCode: string): string => {
        const currencies: Record<string, string> = {
          US: "USD",
          GB: "GBP",
          EU: "EUR",
          CA: "CAD",
          AU: "AUD",
          JP: "JPY",
          CH: "CHF",
          CN: "CNY",
          IN: "INR",
          BR: "BRL",
          RU: "RUB",
          KR: "KRW",
          SG: "SGD",
          HK: "HKD",
          NO: "NOK",
          SE: "SEK",
          DK: "DKK",
          PL: "PLN",
          CZ: "CZK",
          HU: "HUF",
          RO: "RON",
          BG: "BGN",
          HR: "HRK",
          RS: "RSD",
          MK: "MKD",
          AL: "ALL",
          XK: "EUR",
          BA: "BAM",
          ME: "EUR",
          SI: "EUR",
          SK: "EUR",
          LT: "EUR",
          LV: "EUR",
          EE: "EUR",
          FI: "EUR",
          IE: "EUR",
          PT: "EUR",
          ES: "EUR",
          IT: "EUR",
          AT: "EUR",
          DE: "EUR",
          NL: "EUR",
          BE: "EUR",
          LU: "EUR",
          FR: "EUR",
          MT: "EUR",
          CY: "EUR",
          GR: "EUR",
          AD: "EUR",
          MC: "EUR",
          SM: "EUR",
          VA: "EUR",
          LI: "CHF",
          IS: "ISK",
          TR: "TRY",
          GE: "GEL",
          AM: "AMD",
          AZ: "AZN",
          KZ: "KZT",
          UZ: "UZS",
          TM: "TMT",
          TJ: "TJS",
          KG: "KGS",
          MN: "MNT",
          TW: "TWD",
          MO: "MOP",
          TH: "THB",
          VN: "VND",
          LA: "LAK",
          KH: "KHR",
          MY: "MYR",
          ID: "IDR",
          PH: "PHP",
          BN: "BND",
          TL: "USD",
          MM: "MMK",
          NZ: "NZD",
          FJ: "FJD",
          PG: "PGK",
          SB: "SBD",
          VU: "VUV",
          NC: "XPF",
          PF: "XPF",
          WS: "WST",
          TO: "TOP",
          KI: "AUD",
          TV: "AUD",
          NR: "AUD",
          PW: "USD",
          FM: "USD",
          MH: "USD",
          CK: "NZD",
          NU: "NZD",
          TK: "NZD",
          AS: "USD",
          GU: "USD",
          MP: "USD",
          VI: "USD",
          VG: "USD",
          AI: "XCD",
          AW: "AWG",
          CW: "ANG",
          SX: "ANG",
          BQ: "USD",
          KY: "KYD",
          TC: "USD",
          BM: "BMD",
          GL: "DKK",
          PM: "EUR",
          BL: "EUR",
          MF: "EUR",
          GP: "EUR",
          MQ: "EUR",
          AN: "ANG",
          AR: "ARS",
          CL: "CLP",
          CO: "COP",
          PE: "PEN",
          VE: "VES",
          UY: "UYU",
          PY: "PYG",
          BO: "BOB",
          EC: "USD",
          GY: "GYD",
          SR: "SRD",
          GF: "EUR",
          FK: "FKP",
          GS: "GBP",
          MX: "MXN",
          GT: "GTQ",
          BZ: "BZD",
          SV: "USD",
          HN: "HNL",
          NI: "NIO",
          CR: "CRC",
          PA: "PAB",
          CU: "CUP",
          JM: "JMD",
          HT: "HTG",
          DO: "DOP",
          PR: "USD",
          TT: "TTD",
          BB: "BBD",
          AG: "XCD",
          DM: "XCD",
          GD: "XCD",
          KN: "XCD",
          LC: "XCD",
          VC: "XCD",
          BS: "BSD",
          BY: "BYN",
          MD: "MDL",
          UA: "UAH",
          JO: "JOD",
          AE: "AED",
          SA: "SAR",
          QA: "QAR",
          BH: "BHD",
          KW: "KWD",
          OM: "OMR",
          IQ: "IQD",
          SY: "SYP",
          LB: "LBP",
          PS: "ILS",
          EG: "EGP",
          LY: "LYD",
          TN: "TND",
          DZ: "DZD",
          MA: "MAD",
          SD: "SDG",
          SS: "SSP",
          ET: "ETB",
          ER: "ERN",
          DJ: "DJF",
          SO: "SOS",
          KE: "KES",
          UG: "UGX",
          TZ: "TZS",
          RW: "RWF",
          BI: "BIF",
          MW: "MWK",
          ZM: "ZMW",
          ZW: "ZWL",
          BW: "BWP",
          NA: "NAD",
          ZA: "ZAR",
          LS: "LSL",
          SZ: "SZL",
          MG: "MGA",
          MU: "MUR",
          SC: "SCR",
          KM: "KMF",
          YT: "EUR",
          RE: "EUR",
          PK: "PKR",
          BD: "BDT",
          LK: "LKR",
          MV: "MVR",
          NP: "NPR",
          BT: "BTN",
          AF: "AFN",
        };
        return currencies[countryCode] || "USD";
      };

      setCountryData({
        countryCode: detection.countryCode || "US",
        countryName: getCountryName(detection.countryCode || "US"),
        flag: getCountryFlag(detection.countryCode || "US"),
        currency: getCurrency(detection.countryCode || "US"),
        timezone: detection.timezone || "America/New_York",
        city: detection.city,
        region: detection.region,
        isGulfCountry: detection.isGulfCountry || false,
        loading: false,
      });
    }
  }, [detection, gulfLoading]);

  return countryData;
}
